import type { Activity, ReferenceItem } from '../../../app/types';
import type { PractitionerActivityDetail } from '../types';

/** Identifiants d'activités practitioner (API + filtres). */
export const PRACTITIONER_ACTIVITY_IDS = {
  HOME_INTERVENTION: 'homeIntervention',
  PLATFORM: 'platform',
  CRISIS_UNIT: 'crisisUnit',
  CONFLICT_RESOLUTION_SUPPORT: 'conflictResolutionSupport',
  PSE: 'pse',
  APP: 'app',
  COACHING: 'coaching',
  CERTIFIED_COACHING: 'certifiedCoaching',
  PROJECT_MANAGER: 'projectManager',
  HARASSMENT_INVESTIGATION: 'harassmentInvestigation',
  RPS_COACH: 'rpsCoach',
  CALL: 'call',
  VISIO: 'visio',
  WEBINAR: 'webinar',
  /** Filtre legacy encore utilisé dans les exports (hors catalogue UI). */
  AUDIT: 'audit',
} as const;

export type PractitionerActivityId =
  | typeof PRACTITIONER_ACTIVITY_IDS[keyof typeof PRACTITIONER_ACTIVITY_IDS];

const ACTIVITY_LABELS: Record<string, string> = {
  [PRACTITIONER_ACTIVITY_IDS.HOME_INTERVENTION]: 'Intervention à domicile',
  [PRACTITIONER_ACTIVITY_IDS.PLATFORM]: "Ligne d'écoute",
  [PRACTITIONER_ACTIVITY_IDS.CRISIS_UNIT]: 'Cellules de crise',
  [PRACTITIONER_ACTIVITY_IDS.CONFLICT_RESOLUTION_SUPPORT]:
    'Appui à la résolution de conflits, médiation',
  [PRACTITIONER_ACTIVITY_IDS.PSE]: 'Permanence sur site',
  [PRACTITIONER_ACTIVITY_IDS.APP]: 'Analyse de la Pratique Professionnelle',
  [PRACTITIONER_ACTIVITY_IDS.COACHING]: 'Coaching individuel et collectif',
  [PRACTITIONER_ACTIVITY_IDS.CERTIFIED_COACHING]: 'Coaching certifié',
  [PRACTITIONER_ACTIVITY_IDS.PROJECT_MANAGER]: 'Chef de projet/audit',
  [PRACTITIONER_ACTIVITY_IDS.HARASSMENT_INVESTIGATION]: 'Enquête harcèlement',
  [PRACTITIONER_ACTIVITY_IDS.RPS_COACH]:
    'Formateur dans la prévention des RPS auprès de salariés et de managers',
  [PRACTITIONER_ACTIVITY_IDS.CALL]: 'Appel',
  [PRACTITIONER_ACTIVITY_IDS.VISIO]: 'Visio',
  [PRACTITIONER_ACTIVITY_IDS.WEBINAR]: 'Webinaire',
};

/** Catalogue UI des activités practitioner (filtres, formulaires). */
export const PRACTITIONER_ACTIVITIES: Activity[] = [
  {
    id: PRACTITIONER_ACTIVITY_IDS.HOME_INTERVENTION,
    name: ACTIVITY_LABELS[PRACTITIONER_ACTIVITY_IDS.HOME_INTERVENTION],
    '@type': 'Activity',
  },
  {
    id: PRACTITIONER_ACTIVITY_IDS.PLATFORM,
    name: ACTIVITY_LABELS[PRACTITIONER_ACTIVITY_IDS.PLATFORM],
    '@type': 'Activity',
  },
  {
    id: PRACTITIONER_ACTIVITY_IDS.CRISIS_UNIT,
    name: ACTIVITY_LABELS[PRACTITIONER_ACTIVITY_IDS.CRISIS_UNIT],
    '@type': 'Activity',
  },
  {
    id: PRACTITIONER_ACTIVITY_IDS.CONFLICT_RESOLUTION_SUPPORT,
    name: ACTIVITY_LABELS[PRACTITIONER_ACTIVITY_IDS.CONFLICT_RESOLUTION_SUPPORT],
    '@type': 'Activity',
  },
  {
    id: PRACTITIONER_ACTIVITY_IDS.PSE,
    name: ACTIVITY_LABELS[PRACTITIONER_ACTIVITY_IDS.PSE],
    '@type': 'Activity',
  },
  {
    id: PRACTITIONER_ACTIVITY_IDS.APP,
    name: ACTIVITY_LABELS[PRACTITIONER_ACTIVITY_IDS.APP],
    '@type': 'Activity',
  },
  {
    id: PRACTITIONER_ACTIVITY_IDS.COACHING,
    name: ACTIVITY_LABELS[PRACTITIONER_ACTIVITY_IDS.COACHING],
    '@type': 'Activity',
  },
  {
    id: PRACTITIONER_ACTIVITY_IDS.CERTIFIED_COACHING,
    name: ACTIVITY_LABELS[PRACTITIONER_ACTIVITY_IDS.CERTIFIED_COACHING],
    '@type': 'Activity',
  },
  {
    id: PRACTITIONER_ACTIVITY_IDS.PROJECT_MANAGER,
    name: ACTIVITY_LABELS[PRACTITIONER_ACTIVITY_IDS.PROJECT_MANAGER],
    '@type': 'Activity',
  },
  {
    id: PRACTITIONER_ACTIVITY_IDS.HARASSMENT_INVESTIGATION,
    name: ACTIVITY_LABELS[PRACTITIONER_ACTIVITY_IDS.HARASSMENT_INVESTIGATION],
    '@type': 'Activity',
  },
  {
    id: PRACTITIONER_ACTIVITY_IDS.RPS_COACH,
    name: ACTIVITY_LABELS[PRACTITIONER_ACTIVITY_IDS.RPS_COACH],
    '@type': 'Activity',
  },
  {
    id: PRACTITIONER_ACTIVITY_IDS.CALL,
    name: ACTIVITY_LABELS[PRACTITIONER_ACTIVITY_IDS.CALL],
    '@type': 'Activity',
  },
  {
    id: PRACTITIONER_ACTIVITY_IDS.VISIO,
    name: ACTIVITY_LABELS[PRACTITIONER_ACTIVITY_IDS.VISIO],
    '@type': 'Activity',
  },
  {
    id: PRACTITIONER_ACTIVITY_IDS.WEBINAR,
    name: ACTIVITY_LABELS[PRACTITIONER_ACTIVITY_IDS.WEBINAR],
    '@type': 'Activity',
  },
];

/** Paramètres query API Platform pour chaque activité sélectionnée dans les filtres. */
export const PRACTITIONER_ACTIVITY_API_PARAMS: Partial<Record<PractitionerActivityId, string>> = {
  [PRACTITIONER_ACTIVITY_IDS.HOME_INTERVENTION]: 'activity.homeInterventions',
  [PRACTITIONER_ACTIVITY_IDS.APP]: 'activity.app',
  [PRACTITIONER_ACTIVITY_IDS.AUDIT]: 'activity.audit',
  [PRACTITIONER_ACTIVITY_IDS.COACHING]: 'activity.coaching',
  [PRACTITIONER_ACTIVITY_IDS.CONFLICT_RESOLUTION_SUPPORT]: 'activity.conflictResolutionSupport',
  [PRACTITIONER_ACTIVITY_IDS.CRISIS_UNIT]: 'activity.crisisUnit',
  [PRACTITIONER_ACTIVITY_IDS.HARASSMENT_INVESTIGATION]: 'activity.harassmentInvestigation',
  [PRACTITIONER_ACTIVITY_IDS.CERTIFIED_COACHING]: 'activity.certifiedCoaching',
  [PRACTITIONER_ACTIVITY_IDS.PLATFORM]: 'activity.platform',
  [PRACTITIONER_ACTIVITY_IDS.PROJECT_MANAGER]: 'activity.projectManager',
  [PRACTITIONER_ACTIVITY_IDS.PSE]: 'activity.pse',
  [PRACTITIONER_ACTIVITY_IDS.RPS_COACH]: 'activity.rpsCoach',
  [PRACTITIONER_ACTIVITY_IDS.WEBINAR]: 'activity.webinar',
  [PRACTITIONER_ACTIVITY_IDS.CALL]: 'activity.call',
  [PRACTITIONER_ACTIVITY_IDS.VISIO]: 'activity.visio',
};

export function getPractitionerActivityLabel(activityId: string): string {
  return ACTIVITY_LABELS[activityId] ?? activityId;
}

export function toPractitionerActivityReference(activityId: string): ReferenceItem {
  return {
    id: activityId,
    name: getPractitionerActivityLabel(activityId),
    '@type': 'Activity',
  } as ReferenceItem;
}

export function convertPractitionerActivitiesToArray(
  object: PractitionerActivityDetail,
): ReferenceItem[] {
  const items: ReferenceItem[] = [];

  Object.keys(object).forEach((key) => {
    const value = object[key as keyof PractitionerActivityDetail];
    if (typeof value === 'boolean' && value) {
      items.push(toPractitionerActivityReference(key));
    }
  });

  return items;
}

export function convertPractitionerActivitiesToObject(
  tab: Array<{ id: string }>,
): PractitionerActivityDetail {
  const selectedIds = new Set(tab.map((item) => item.id));

  return {
    homeIntervention: selectedIds.has(PRACTITIONER_ACTIVITY_IDS.HOME_INTERVENTION),
    platform: selectedIds.has(PRACTITIONER_ACTIVITY_IDS.PLATFORM),
    crisisUnit: selectedIds.has(PRACTITIONER_ACTIVITY_IDS.CRISIS_UNIT),
    conflictResolutionSupport: selectedIds.has(
      PRACTITIONER_ACTIVITY_IDS.CONFLICT_RESOLUTION_SUPPORT,
    ),
    pse: selectedIds.has(PRACTITIONER_ACTIVITY_IDS.PSE),
    app: selectedIds.has(PRACTITIONER_ACTIVITY_IDS.APP),
    coaching: selectedIds.has(PRACTITIONER_ACTIVITY_IDS.COACHING),
    certifiedCoaching: selectedIds.has(PRACTITIONER_ACTIVITY_IDS.CERTIFIED_COACHING),
    projectManager: selectedIds.has(PRACTITIONER_ACTIVITY_IDS.PROJECT_MANAGER),
    harassmentInvestigation: selectedIds.has(
      PRACTITIONER_ACTIVITY_IDS.HARASSMENT_INVESTIGATION,
    ),
    call: selectedIds.has(PRACTITIONER_ACTIVITY_IDS.CALL),
    visio: selectedIds.has(PRACTITIONER_ACTIVITY_IDS.VISIO),
    rpsCoach: selectedIds.has(PRACTITIONER_ACTIVITY_IDS.RPS_COACH),
    webinar: selectedIds.has(PRACTITIONER_ACTIVITY_IDS.WEBINAR),
  };
}

export function isPractitionerActivitySelected(
  activities: Array<{ id?: string }>,
  activityId: PractitionerActivityId,
): boolean {
  return activities.some((item) => item.id === activityId);
}
